import { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  Alert, TextField, Select, MenuItem, FormControl, InputLabel, Button, IconButton
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { reportsAPI } from "../../services/api";

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [lines, setLines] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(false); // off by default
  const intervalRef = useRef(null);

  const loadLogs = async () => {
    try {
      const res = await reportsAPI.getActivityLog(lines);
      setLogs(res.data.logs || []);
    } catch { setError("Failed to load activity log."); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadLogs(); }, [lines]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(loadLogs, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, lines]);

  const filteredLogs = logs.map(l => ({...l, user: l.user || l.user_email, status: l.status || String(l.status_code), duration: l.duration || (l.duration_ms ? l.duration_ms + 'ms' : '')})).filter(l => {
    const matchText = !filter || l.path?.toLowerCase().includes(filter.toLowerCase());
    const matchMethod = !methodFilter || l.method === methodFilter;
    const matchStatus = !statusFilter || String(l.status_code || l.status || '').startsWith(statusFilter);
    return matchText && matchMethod && matchStatus;
  });

  const statusColor = (status) => {
    if (!status) return "default";
    const code = parseInt(status);
    if (code >= 500) return "error";
    if (code >= 400) return "warning";
    if (code >= 200) return "success";
    return "default";
  };

  const methodColor = (method) => {
    const colors = { GET: "#1976d2", POST: "#2e7d32", PUT: "#e65100", DELETE: "#c62828", PATCH: "#6a1b9a" };
    return colors[method] || "#666";
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#1a237e">Activity Log</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant={autoRefresh ? "contained" : "outlined"}
            size="small"
            onClick={() => setAutoRefresh(v => !v)}
            style={autoRefresh ? { background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" } : {}}>
            {autoRefresh ? "⏸ Auto Refresh ON" : "▶ Auto Refresh"}
          </Button>
          <IconButton onClick={loadLogs} size="small"><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Card sx={{ borderRadius: 3, boxShadow: 1, mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" label="Search path" value={filter}
              onChange={e => setFilter(e.target.value)} sx={{ minWidth: 200 }} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Method</InputLabel>
              <Select value={methodFilter} label="Method" onChange={e => setMethodFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
                <MenuItem value="PATCH">PATCH</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="2">2xx Success</MenuItem>
                <MenuItem value="4">4xx Client Error</MenuItem>
                <MenuItem value="5">5xx Server Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Show</InputLabel>
              <Select value={lines} label="Show" onChange={e => setLines(e.target.value)}>
                <MenuItem value={50}>Last 50</MenuItem>
                <MenuItem value={100}>Last 100</MenuItem>
                <MenuItem value={200}>Last 200</MenuItem>
                <MenuItem value={500}>Last 500</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              {filteredLogs.length} entries
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><b>Timestamp</b></TableCell>
                    <TableCell><b>Method</b></TableCell>
                    <TableCell><b>User</b></TableCell>
                    <TableCell><b>Action</b></TableCell>
                    <TableCell><b>Path</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell><b>Duration</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.map((log, i) => (
                    <TableRow key={i} hover sx={{
                      bgcolor: log.level === "WARNING" ? "#fff8e1" : "inherit"
                    }}>
                      <TableCell sx={{ fontSize: 11, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {log.timestamp}
                      </TableCell>
                      <TableCell>
                        <Chip label={log.method} size="small"
                          sx={{ bgcolor: methodColor(log.method), color: "#fff", fontWeight: 700, fontSize: 10 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: "#1a237e" }}>{log.user || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: log.description ? "#1a237e" : "text.primary" }}>{log.description || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 11, fontFamily: "monospace", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "text.secondary" }}>
                        {log.path}
                      </TableCell>
                      <TableCell>
                        <Chip label={log.status} size="small" color={statusColor(log.status)} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: parseFloat(log.duration) > 1000 ? "error.main" : "text.secondary" }}>
                        {log.duration}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredLogs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No log entries found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
